import React, {useMemo} from "react"
import {Box, Divider, Link, Typography} from "@mui/material"
import Markdown, {type Components} from "react-markdown"

import {colors} from "@indebted/theme/colors"

// Everything CommonMark produces except images. Anything outside this list
// loses its markup and keeps its text; raw HTML always renders as text.
const rendered = [
	"p",
	"br",
	"a",
	"strong",
	"em",
	"ul",
	"ol",
	"li",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"code",
	"pre",
	"blockquote",
	"hr",
]

type RuleTextProps = {
	text: string
	manualIDs?: string[]
	onOpenManual?: (id: string) => void
}

const noManualIDs: string[] = []

// Rule text is markdown, rendered as React nodes rather than markup, so it
// can never introduce anything but the elements above and the links it declares.
function RuleText({text, manualIDs = noManualIDs, onOpenManual}: RuleTextProps): JSX.Element {
	const withAnchor = useMemo(() => ({...components, a: anchor(manualIDs, onOpenManual)}), [manualIDs, onOpenManual])
	return (
		<Markdown allowedElements={rendered} unwrapDisallowed urlTransform={linkable} components={withAnchor}>
			{text}
		</Markdown>
	)
}

// http(s) links reach the browser; rule: references reach the anchor renderer,
// which resolves them against the case's manuals. Anything else loses its href.
function linkable(url: string): string | undefined {
	return /^(https?:\/\/|rule:)/i.test(url) ? url : undefined
}

const ruleScheme = /^rule:/i

// A rule: reference is a chip when the case carries the manual it names and a
// handler can open it; otherwise it reads as plain text, like any stripped link.
function anchor(manualIDs: string[], onOpenManual?: (id: string) => void): Components["a"] {
	return function Anchor({href, children}) {
		if (href && ruleScheme.test(href)) {
			const id = href.replace(ruleScheme, "")
			if (!onOpenManual || !manualIDs.includes(id)) return <>{children}</>
			return (
				<Link
					component="button"
					variant="body2"
					underline="none"
					onClick={() => onOpenManual(id)}
					sx={{
						color: colors.warmBlue["900"],
						bgcolor: colors.warmBlue["25"],
						px: 1,
						borderRadius: "999px",
						"&:hover": {bgcolor: colors.warmBlue["50"]},
					}}
				>
					{children}
				</Link>
			)
		}
		return href ? (
			<Link href={href} target="_blank" rel="noopener noreferrer" sx={{color: colors.warmBlue["900"]}}>
				{children}
			</Link>
		) : (
			<>{children}</>
		)
	}
}

const heading: Components["h1"] = ({children}) => (
	<Typography variant="body2" component="h6" fontWeight={600} mt={1}>
		{children}
	</Typography>
)

const monospace = {fontFamily: "monospace", fontSize: "0.8125rem"}

const components: Components = {
	p: ({children}) => (
		<Typography variant="body2" color="textPrimary">
			{children}
		</Typography>
	),
	ul: ({children}) => (
		<Box component="ul" m={0} pl={5} display="flex" flexDirection="column" gap={0.5}>
			{children}
		</Box>
	),
	ol: ({children, start}) => (
		<Box component="ol" start={start} m={0} pl={5} display="flex" flexDirection="column" gap={0.5}>
			{children}
		</Box>
	),
	li: ({children}) => (
		<Typography component="li" variant="body2">
			{children}
		</Typography>
	),
	h1: heading,
	h2: heading,
	h3: heading,
	h4: heading,
	h5: heading,
	h6: heading,
	code: ({children}) => (
		<Box component="code" sx={{...monospace, bgcolor: colors.gray["100"], px: 0.5, borderRadius: 1}}>
			{children}
		</Box>
	),
	pre: ({children}) => (
		<Box
			component="pre"
			sx={{
				...monospace,
				m: 0,
				p: 2,
				bgcolor: colors.gray["50"],
				border: `1px solid ${colors.gray["200"]}`,
				borderRadius: 1,
				overflowX: "auto",
				whiteSpace: "pre-wrap",
				"& code": {bgcolor: "transparent", p: 0},
			}}
		>
			{children}
		</Box>
	),
	blockquote: ({children}) => (
		<Box
			component="blockquote"
			sx={{m: 0, pl: 2, borderLeft: `2px solid ${colors.gray["300"]}`, color: "text.secondary"}}
		>
			{children}
		</Box>
	),
	hr: () => <Divider sx={{my: 1}} />,
}

export {RuleText}
